import {OnChangeFn} from "@tanstack/react-table";
import classNames from "classnames";
import {ReactNode} from "react";
import {FiPlus} from "react-icons/fi";
import {Link} from "react-router";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {TwoLineShortDateTimeUser} from "@/components/datetime/TwoLineShortDateTimeUser.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {ActiveOccurrencesListResponseValues} from "@/lib/queries/maintenance.ts";
import {MaintenanceOccurrence} from "@/lib/types/maintenance.ts";
import {AffectedChecksCell} from "@/pages/maintenance/maintenance-dashboard/components/AffectedChecksCell/AffectedChecksCell.tsx";
import {ActiveOccurrenceActions} from "@/pages/maintenance/maintenance-dashboard/tabs/active-occurrences/actions/ActiveOccurrenceActions.tsx";
import {ActiveOccurrenceDuration} from "@/pages/maintenance/maintenance-dashboard/tabs/active-occurrences/ActiveOccurrencesTable/components/ActiveOccurrenceDuration.tsx";
import {ActiveOccurrenceEnds} from "@/pages/maintenance/maintenance-dashboard/tabs/active-occurrences/ActiveOccurrencesTable/components/ActiveOccurrenceEnds.tsx";

import styles from "./ActiveOccurrencesTable.module.scss";

type ActiveOccurrencesTableProps = {
  data?: ActiveOccurrencesListResponseValues;
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  pagination: LimitAndOffset;
  setPagination: (pagination: LimitAndOffset) => void;
  isFiltered?: boolean;
  loading?: boolean;
  rowSelection?: Record<string, boolean>;
  setRowSelection?: OnChangeFn<Record<string, boolean>>;
  actionBar?: ReactNode;
};

export const ActiveOccurrencesTable = ({
  data,
  sortColumn,
  sortDirection,
  onSortingChange,
  pagination,
  setPagination,
  isFiltered,
  loading,
  rowSelection,
  setRowSelection,
  actionBar,
}: ActiveOccurrencesTableProps) => {
  const {user} = useCurrentUserContext();

  const specs: ColumnSpec<MaintenanceOccurrence>[] = [
    {
      fieldName: "name",
      fieldTitle: "Name",
      enableSorting: true,
      meta: {
        headerClassName: styles.nameColumn,
      },
      accessor: (occurrence: MaintenanceOccurrence) => (
        <Tooltip tooltipContent={occurrence.schedule.name} delayMs={500}>
          <div className="ellipsis-overflow pulseTextBodySmall pulse-color-gray-900">{occurrence.schedule.name}</div>
        </Tooltip>
      ),
    },
    {
      fieldName: "starts_at",
      fieldTitle: "Started",
      enableSorting: true,
      meta: {
        headerClassName: classNames("text-nowrap", styles.startedColumn),
      },
      accessor: (occurrence: MaintenanceOccurrence) => (
        <TwoLineShortDateTimeUser date={occurrence.starts_at} user={user!} />
      ),
    },
    {
      fieldName: "ends_at",
      fieldTitle: "Ends",
      enableSorting: true,
      meta: {
        headerClassName: classNames("text-nowrap", styles.endsColumn),
      },
      accessor: (occurrence: MaintenanceOccurrence) => <ActiveOccurrenceEnds occurrence={occurrence} user={user!} />,
    },
    {
      fieldName: "duration",
      fieldTitle: "Duration",
      meta: {
        headerClassName: classNames("text-nowrap", styles.durationColumn),
      },
      accessor: (occurrence: MaintenanceOccurrence) => <ActiveOccurrenceDuration occurrence={occurrence} />,
    },
    {
      fieldName: "services",
      fieldTitle: "Affected Checks",
      enableSorting: true,
      meta: {
        headerClassName: classNames(styles.checksColumn),
      },
      accessor: (occurrence: MaintenanceOccurrence) => (
        <AffectedChecksCell
          services={occurrence.services}
          count={occurrence.services.length}
          modelId={occurrence.id}
          modelType={"MaintenanceScheduleOccurrence"}
          title={"Maintenance: " + occurrence.schedule.name}
        />
      ),
    },
    ...(user?.has_write_access
      ? [
          {
            fieldName: "actions",
            meta: {
              headerClassName: styles.actionColumn,
            },
            accessor: (occurrence: MaintenanceOccurrence) => <ActiveOccurrenceActions occurrence={occurrence} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <Table
        rows={data?.items || []}
        specs={specs}
        initialState={{columnPinning: {right: ["actions"]}}}
        sorting={{column: sortColumn, direction: sortDirection}}
        onSortingChange={onSortingChange}
        isLoading={loading}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        getRowId={(row) => String(row.id)}
        actionBar={actionBar}
        noDataComponent={
          <EmptyTable colSpan={specs.length}>
            {isFiltered ? (
              <div className="d-flex">No active maintenance windows found matching the filter(s) above.</div>
            ) : (
              <div className="d-flex flex-column align-items-center gap-3 py-5">
                <div className="pulse-color-gray-800 pulseTextBodyBold">There are no active maintenance windows.</div>
                <Button variant={"secondary"} size={"large"} as={Link} to={"/maintenance/schedules/create"}>
                  <Icon icon={FiPlus} /> Create Schedule
                </Button>
              </div>
            )}
          </EmptyTable>
        }
        fixedWidth={true}
        minWidth={"1026px"}
        pagination={pagination}
        updatePagination={setPagination}
        totalRows={data?.total || 0}
        paginationData={data?.items}
      />
    </>
  );
};
